import { useQueryClient, useQuery as _useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import {
  DjangoClientInvalidateQueryFilters,
  DjangoClientMethod,
  DjangoClientPath,
  DjangoClientQueryFnData,
  DjangoCLientQueryFnParams,
} from "../types/DjangoClient.types";
import { isEqual } from "lodash";

const getQueryKey = <Params extends (string | number)[]>(
  path: DjangoClientPath,
  method: DjangoClientMethod,
  ...params: Params
) => [path, method, ...params];

const useInvalidateQuery = <
  Path extends DjangoClientPath,
  Method extends DjangoClientMethod,
  Params extends (string | number)[],
>(
  path: Path,
  method: Method,
  ...params: Params
) => {
  const queryKey = getQueryKey(path, method, ...params);
  const [queryKeyMemo, setQueryKeyMemo] = useState(queryKey);

  /**
   * Memoize query key
   * React Query is smart enough to compare query keys
   * element by element, but an invalidate function from a hook
   * that depends on a query key array is not, so this memoizes
   * that array reference
   *
   */
  useEffect(() => {
    setQueryKeyMemo((prev) => {
      if (!isEqual(prev, queryKey)) {
        return queryKey;
      }
      return prev;
    });
  }, [queryKey]);

  const queryClient = useQueryClient();

  const invalidateQuery = useCallback(
    (filters: DjangoClientInvalidateQueryFilters) => {
      return queryClient.invalidateQueries({
        queryKey: queryKeyMemo,
        ...filters,
      });
    },
    [queryClient, queryKeyMemo],
  );

  return { invalidateQuery };
};

export const djangoClientReactQueryFactory = <
  Path extends DjangoClientPath,
  Method extends DjangoClientMethod,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Fn extends (...args: any[]) => Promise<any>,
>(
  path: Path,
  method: Method,
  queryFunction: Fn,
) => {
  const useQuery = (...params: DjangoCLientQueryFnParams<Fn>) => {
    const queryKey = getQueryKey(path, method, ...params);
    const { invalidateQuery } = useInvalidateQuery(path, method, ...params);

    const res = _useQuery<DjangoClientQueryFnData<Fn>>({
      queryKey,
      queryFn: () => queryFunction(...params),
    });
    return { ...res, invalidateQuery };
  };

  return useQuery;
};
